import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Search } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar'
import { Carousel, CarouselContent, CarouselItem } from '../ui/carousel'
import { DashboardSection } from './DashboardSection'
import type { DashboardGym } from '../../types/dashboard'

/** dragFree drops Embla's snap points, so the strip scrolls without anchoring. */
const FREE_SCROLL = { align: 'start', dragFree: true, containScroll: 'trimSnaps' } as const

interface GymsCarouselProps {
  gyms: DashboardGym[]
}

export function GymsCarousel({ gyms }: GymsCarouselProps) {
  const { t } = useTranslation()

  return (
    <DashboardSection title={t('dashboard.gyms.title')}>
      <Carousel opts={FREE_SCROLL} className="w-full">
        <CarouselContent className="-ml-3">
          {gyms.length === 0 ? (
            <CarouselItem className="basis-auto pl-3">
              <Link
                to="/search"
                className="group flex w-14 flex-col items-center gap-1.5"
                aria-label={t('dashboard.gyms.findGym')}
              >
                <span className="flex size-11 items-center justify-center rounded-full border border-dashed border-muted-foreground/40 text-muted-foreground transition-colors group-hover:border-primary group-hover:text-primary">
                  <Search className="size-4" />
                </span>
                <span className="w-full truncate text-center text-[10px] font-medium text-muted-foreground">
                  {t('dashboard.gyms.findGym')}
                </span>
              </Link>
            </CarouselItem>
          ) : (
            gyms.map((gym) => (
              <CarouselItem key={gym.id} className="basis-auto pl-3">
                <Link to={`/gyms/${gym.slug}`} className="group flex w-14 flex-col items-center gap-1.5">
                  <Avatar className="size-11 border transition-colors group-hover:border-primary">
                    <AvatarImage src={gym.logo_small_url ?? undefined} alt={gym.name} loading="lazy" />
                    <AvatarFallback className="text-xs">
                      {gym.name.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="w-full truncate text-center text-[10px] font-medium">{gym.name}</span>
                </Link>
              </CarouselItem>
            ))
          )}
        </CarouselContent>
      </Carousel>
    </DashboardSection>
  )
}
